import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Banner,
  Button,
  Dialog,
  Input,
  PageHead,
  Panel,
  ProgressBar,
  Spinner,
  Tabs,
} from "@guestpilot/ui-kit";
import { Copy16Regular } from "@fluentui/react-icons";
import { useGet, useWrite } from "../api/hooks";
import type { AvailableTeam, Schema, Subscription, Team } from "../api/types";
import { TeamsTable, worstRisk } from "../components/TeamsTable";
import { ApiProblemError } from "../api/client";
import { useHandleApiError } from "../errors";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { runConsent } from "../lib/consent";

type Activation = Schema<"ActivationInfo">;
const FILTERS = ["all", "needs_review", "at_risk"] as const;
type Filter = (typeof FILTERS)[number];

/** Controlled Teams: the plan-limited resource (BR-020, BR-199). Tabs filter on review status and on any nonzero risk. */
export function Teams() {
  const { t } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS as readonly string[]).includes(params.get("filter") ?? "")
    ? (params.get("filter") as Filter)
    : "all";
  const all = useGet<Team[]>("/api/v1/teams", { filter: "all" });
  const sub = useGet<Subscription>("/api/v1/subscription", undefined, {
    enabled: s.can("SUBSCRIPTION_READ"),
  });
  const rows = all.data;
  const active = (rows ?? []).filter((r) => r.status !== "deactivated");
  const needs = active.filter((r) => r.reviewStatus === "not_started");
  const atRisk = active.filter((r) => worstRisk(r.riskCounts) !== null);
  const shown = filter === "all" ? active : filter === "needs_review" ? needs : atRisk;
  const used = sub.data?.teamsUsed ?? active.filter((r) => r.status === "active").length;
  const limit = sub.data?.effectiveTeamLimit;
  const open = params.get("activate") === "1";

  return (
    <div className="gp-page">
      <PageHead
        title={t("ui:teams.title")}
        subtitle={t("ui:teams.subtitle")}
        actions={
          <Button
            variant="primary"
            disabledReason={s.gate("TEAM_ACTIVATE")}
            onClick={() => setParams({ activate: "1" })}
          >
            {t("ui:teams.activate")}
          </Button>
        }
      />
      {limit !== undefined ? (
        <div className="gp-card" aria-label={t("ui:teams.usage")}>
          <span className="t-body-strong">
            {limit === -1 ? t("ui:teams.usageUnlimited", { used }) : t("ui:teams.usageOf", { used, limit })}
          </span>
          {limit !== -1 ? (
            <ProgressBar value={used} max={limit} label={t("ui:teams.usageOf", { used, limit })} />
          ) : null}
          <span className="t-body-sm gp-muted">
            {sub.data?.status === "trial"
              ? t("ui:teams.trialPlan")
              : t(`ui:plan.${sub.data?.planKey ?? "starter"}`)}
          </span>
        </div>
      ) : null}
      <Tabs
        label={t("ui:teams.filters")}
        value={filter}
        onChange={(k) => setParams(k === "all" ? {} : { filter: k })}
        tabs={[
          { key: "all", label: t("ui:teams.tab.all"), count: active.length },
          { key: "needs_review", label: t("ui:teams.tab.needs"), count: needs.length },
          { key: "at_risk", label: t("ui:teams.tab.risk"), count: atRisk.length },
        ]}
      />
      <Panel label={t("ui:teams.title")}>
        <TeamsTable
          rows={rows ? shown : undefined}
          loading={all.isLoading}
          error={all.isError}
          onRetry={() => void all.refetch()}
          tz={s.me.tenant.timeZone}
          emptyAction={
            <Button
              variant="primary"
              disabledReason={s.gate("TEAM_ACTIVATE")}
              onClick={() => nav("?activate=1")}
            >
              {t("ui:teams.activate")}
            </Button>
          }
        />
      </Panel>
      <ActivateGuide
        open={open}
        onClose={() => setParams({})}
        limitReached={limit !== undefined && limit !== -1 && used >= limit}
      />
    </div>
  );
}

/**
 * "Activate a Team" (BR-021, BR-272, BR-273): the add-to-Team link is the main path (an Owner adds the app, GuestPilot activates it);
 * with one-click enabled an Admin picks from the Teams they belong to; Teams waiting for a free slot can be retried.
 */
export function ActivateGuide({
  open,
  onClose,
  limitReached,
}: {
  open: boolean;
  onClose: () => void;
  limitReached: boolean;
}) {
  const { t } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const fail = useHandleApiError();
  const info = useGet<Activation>("/api/v1/activation", undefined, { enabled: open });
  const oneClick = info.data?.oneClick?.enabled === true;
  const avail = useGet<AvailableTeam[]>("/api/v1/teams/available", undefined, { enabled: open && oneClick });
  const activate = useWrite<Team, { graphTeamId: string }>("POST", "/api/v1/teams", [
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/subscription",
    "/api/v1/onboarding",
    "/api/v1/activation",
  ]);
  const retry = useWrite<Team>("POST", "/api/v1/teams/:teamId/activate", [
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/activation",
  ]);
  // automatic adding is off by default: the Admin can turn it on from here (BR-273); Microsoft asks for the permission once
  const enableOneClick = useWrite<Activation, Schema<"OneClickChange">>(
    "PUT",
    "/api/v1/activation/one-click",
    ["/api/v1/activation"],
  );
  const [enabling, setEnabling] = useState(false);
  const [autoFailed, setAutoFailed] = useState(false);
  const turnOnAutomatic = () => {
    setAutoFailed(false);
    enableOneClick.mutate(
      { body: { enabled: true } },
      {
        onError: fail,
        onSuccess: async (a) => {
          const url = a.oneClick?.consentUrl;
          if (!url) return void info.refetch();
          setEnabling(true);
          try {
            const done = await runConsent(
              url,
              async () => (await info.refetch()).data?.oneClick?.consent === "granted",
            );
            if (!done) setAutoFailed(true);
            await info.refetch();
          } finally {
            setEnabling(false);
          }
        },
      },
    );
  };
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");
  const [blocked, setBlocked] = useState(false);
  const waiting = info.data?.waitingTeams ?? [];
  const showUpgrade = limitReached || blocked || waiting.length > 0;

  const onError = (e: unknown) => {
    if (
      e instanceof ApiProblemError &&
      (e.problem.code === "limit_reached" || e.problem.code === "activation_cap_reached")
    )
      setBlocked(true);
    else fail(e);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t("ui:activate.title")}
      description={t("ui:activate.lead")}
      footer={<Button onClick={onClose}>{t("common:close")}</Button>}
    >
      {info.isLoading ? <Spinner label={t("common:loading")} /> : null}
      {showUpgrade ? (
        <Banner
          tone="warn"
          action={
            s.can("SUBSCRIPTION_READ") ? (
              <Button onClick={() => nav("/subscription")}>{t("ui:activate.upgrade")}</Button>
            ) : undefined
          }
        >
          {t("ui:activate.limit")}
        </Banner>
      ) : null}
      {info.data?.addToTeamLink && !oneClick ? (
        <div className="gp-stack">
          <h3 className="t-body-strong" style={{ margin: 0 }}>
            {t("ui:activate.linkTitle")}
          </h3>
          <p className="t-body gp-muted" style={{ margin: 0 }}>
            {t("ui:activate.linkBody")}
          </p>
          <div className="gp-row">
            <code className="t-mono t-body-sm" style={{ overflowWrap: "anywhere" }}>
              {info.data.addToTeamLink}
            </code>
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(info.data?.addToTeamLink ?? "");
                setCopied(true);
              }}
            >
              <Copy16Regular aria-hidden /> {copied ? t("ui:activate.copied") : t("ui:enroll.copy")}
            </Button>
          </div>
        </div>
      ) : null}
      {waiting.length > 0 ? (
        <div className="gp-stack">
          <h3 className="t-body-strong" style={{ margin: 0 }}>
            {t("ui:activate.waiting")}
          </h3>
          {waiting.map((w) => (
            <div key={w.id} className="gp-row" style={{ justifyContent: "space-between" }}>
              <span className="t-body">{w.displayName}</span>
              <Button
                disabledReason={s.gate("TEAM_ACTIVATE")}
                onClick={() => retry.mutate({ params: { teamId: w.id } }, { onError })}
              >
                {t("ui:activate.retry")}
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {oneClick ? (
        <div className="gp-stack">
          <h3 className="t-body-strong" style={{ margin: 0 }}>
            {t("ui:activate.oneClick")}
          </h3>
          {(avail.data ?? []).length > 5 ? (
            <Input
              type="search"
              aria-label={t("ui:activate.search")}
              placeholder={t("ui:activate.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          ) : null}
          {avail.isLoading ? <Spinner label={t("common:loading")} /> : null}
          {(avail.data ?? []).length === 0 && !avail.isLoading ? (
            <p className="t-body gp-muted" style={{ margin: 0 }}>
              {t("ui:activate.noneAvailable")}
            </p>
          ) : null}
          {(avail.data ?? [])
            .filter((a) => a.displayName.toLowerCase().includes(search.trim().toLowerCase()))
            .map((a) => (
              <div key={a.graphTeamId} className="gp-row" style={{ justifyContent: "space-between" }}>
                <span className="t-body">{a.displayName}</span>
                <Button
                  variant="primary"
                  disabledReason={s.gate("TEAM_ACTIVATE")}
                  onClick={() =>
                    activate.mutate(
                      { body: { graphTeamId: a.graphTeamId } },
                      { onError, onSuccess: () => void avail.refetch() },
                    )
                  }
                >
                  {t("ui:activate.action")}
                </Button>
              </div>
            ))}
        </div>
      ) : info.data ? (
        <div className="gp-stack">
          <h3 className="t-body-strong" style={{ margin: 0 }}>
            {t("ui:activate.autoTitle")}
          </h3>
          <p className="t-body gp-muted" style={{ margin: 0 }}>
            {t("ui:activate.autoBody")}
          </p>
          {autoFailed ? (
            <p className="t-body" role="alert" style={{ margin: 0 }}>
              {t("ui:activate.autoFailed")}
            </p>
          ) : null}
          <div className="gp-actions">
            <Button
              variant="primary"
              disabledReason={
                s.gate("TEAM_ACTIVATE") ??
                (enabling || enableOneClick.isPending ? t("common:loading") : undefined)
              }
              onClick={turnOnAutomatic}
            >
              {enabling ? t("common:loading") : t("ui:activate.autoEnable")}
            </Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
